import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Camera, Crown, Loader2, Sparkles as SparklesIcon, ImagePlus } from "lucide-react";
import Sparkles from "../components/Sparkles";
import CropModal from "../components/CropModal";
import LangToggle from "../components/LangToggle";
import RoyalJourney from "../components/RoyalJourney";
import { getRoles, registerGuest } from "../lib/api";
import { useLang, t } from "../lib/i18n";

const FALLBACK_ROLES = [
  { key: "father",        title_te: "ధైర్యపు మహారాజు",        title_en: "Maharaja of Courage",         te: "నాన్న",                    en: "Father" },
  { key: "mother",        title_te: "ప్రేమ మహాసామ్రాజ్ఞి",     title_en: "Grand Empress of Love",       te: "అమ్మ",                     en: "Mother" },
  { key: "grandfather",   title_te: "కథల మహారాజు",             title_en: "Maharaja of Stories",         te: "తాతయ్య",                   en: "Grandfather" },
  { key: "grandmother",   title_te: "ఆశీర్వాదాల మహారాణి",       title_en: "Empress of Blessings",        te: "అమ్మమ్మ",                   en: "Maternal Grandmother" },
  { key: "peddananna",    title_te: "రాజ్య సంరక్షక మహారాజు",   title_en: "Guardian Maharaja",           te: "పెద్దనాన్న",                 en: "Father's Elder Brother" },
  { key: "peddamma",      title_te: "వాత్సల్య మహారాణి",         title_en: "Empress of Affection",        te: "పెద్దమ్మ",                   en: "Wife of Peddananna" },
  { key: "chinnanna",     title_te: "చాక్లెట్ నిధి సంరక్షకుడు",   title_en: "Guardian of Chocolate Treasure", te: "చిన్నాన్న / బాబాయి",   en: "Father's Younger Brother" },
  { key: "pinni",         title_te: "ముద్దుల మాయాదేవత",         title_en: "Fairy of Sweet Kisses",       te: "పిన్ని",                    en: "Mother's Younger Sister / Chinnanna's Wife" },
  { key: "attha",         title_te: "పూల మాల మహారాణి",         title_en: "Empress of Flower Garlands",  te: "అత్త",                      en: "Father's Sister" },
  { key: "mamayya",       title_te: "ఆశ్చర్యాల మామయ్య",         title_en: "Uncle of Wonders",            te: "మామయ్య",                    en: "Husband of Attha" },
  { key: "friend_male",   title_te: "ఆనంద కేరింతల వీరుడు",     title_en: "Warrior of Joyful Giggles",   te: "కుటుంబ స్నేహితుడు",         en: "Family Friend (Male)" },
  { key: "friend_female", title_te: "నవ్వుల స్నేహ తార",         title_en: "Star of Sweet Friendship",    te: "కుటుంబ స్నేహితురాలు",       en: "Family Friend (Female)" },
];

export default function Register() {
  const lang = useLang();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const cameraRef = useRef(null);
  const [roles, setRoles] = useState(FALLBACK_ROLES);
  const [name, setName] = useState("");
  const [relationship, setRelationship] = useState("");
  const [rawFile, setRawFile] = useState(null);
  const [showCropper, setShowCropper] = useState(false);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    getRoles().then((rs) => {
      // Merge backend roles with fallback to keep bilingual fields
      const merged = rs.map((r) => {
        const fb = FALLBACK_ROLES.find((f) => f.key === r.key) || {};
        return { ...fb, ...r };
      });
      setRoles(merged.length ? merged : FALLBACK_ROLES);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!file) {
      setPreview(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!name.trim() || !relationship || !file) {
      setError(t("reg_error_fields", lang));
      return;
    }
    setSubmitting(true);
    try {
      const res = await registerGuest({ name: name.trim(), relationship, selfie: file });
      // Carry kiosk flag forward so the reveal page auto-returns to the kiosk
      const isKiosk = new URLSearchParams(window.location.search).get("kiosk") === "1";
      navigate(`/reveal/${res.id}${isKiosk ? "?kiosk=1" : ""}`);
    } catch (err) {
      setError(err?.response?.data?.detail || "Something went wrong. Try again.");
      setSubmitting(false);
    }
  };

  const roleTitle = (r) => (lang === "en" ? (r.title_en || r.title) : (r.title_te || r.title));

  return (
    <div className="relative min-h-screen px-6 sm:px-12 py-6 sm:py-8" style={{ fontFamily: "'Noto Sans Telugu','Outfit',sans-serif" }}>
      {showCropper && rawFile && (
        <CropModal
          file={rawFile}
          onCancel={() => {
            setShowCropper(false);
            if (!file) setRawFile(null);
          }}
          onConfirm={(cropped) => {
            setFile(cropped);
            setShowCropper(false);
          }}
        />
      )}
      <Sparkles count={20} />
      <div className="relative max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-2">
          <Link to="/welcome" className="label-eyebrow text-ink-soft hover:text-plum">
            ← {t("back", lang)}
          </Link>
          <LangToggle />
        </div>

        <div className="mt-2 mb-3 text-center sm:text-left">
          <p className="font-script text-sm sm:text-base text-plum leading-tight">{t("reg_eyebrow", lang)}</p>
          <h1 className="font-display text-lg sm:text-xl font-semibold text-ink leading-tight">
            {t("reg_title_a", lang)} <span className="gold-text">{t("reg_title_b", lang)}</span> {t("reg_title_c", lang)}
          </h1>
        </div>

        <RoyalJourney />

        <form
          onSubmit={onSubmit}
          className="glass-deep rounded-3xl p-6 sm:p-10 space-y-7 relative"
          style={{
            boxShadow:
              "0 0 0 2px rgba(212,175,55,0.55), 0 22px 60px -18px rgba(228,73,125,0.35), 0 12px 30px -12px rgba(74,22,53,0.25)",
          }}
          data-testid="register-form"
        >
          {/* Highlight ribbon — draws the eye to the form */}
          <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-[11px] tracking-[0.24em] uppercase font-semibold shadow"
               style={{
                 background: "linear-gradient(90deg,#F6D77A,#E4497D,#F6D77A)",
                 color: "#4A1635",
                 fontFamily: "'Tiro Telugu','Noto Sans Telugu',serif",
               }}
          >
            ✦ {lang === "te" ? "మీ వివరాలు · సెల్ఫీ" : "Your Details · Selfie"} ✦
          </div>
          <div>
            <label className="label-eyebrow block mb-2">{t("reg_name_label", lang)}</label>
            <input
              data-testid="register-name-input"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("reg_name_ph", lang)}
              className="w-full bg-transparent border-0 border-b-2 border-[#D4AF37]/40 px-0 py-2 text-xl text-ink focus:outline-none focus:border-[#D4AF37] placeholder:text-ink-soft/50 font-display"
              maxLength={40}
            />
          </div>

          <div>
            <label className="label-eyebrow block mb-2">{t("reg_rel_label", lang)}</label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {roles.map((r) => (
                <button
                  key={r.key}
                  type="button"
                  data-testid={`role-${r.key}`}
                  onClick={() => setRelationship(r.key)}
                  className={`text-left rounded-2xl px-3 py-2.5 transition-all border ${
                    relationship === r.key
                      ? "bg-[#D4AF37]/15 border-[#D4AF37] shadow-inner"
                      : "bg-white/40 border-transparent hover:border-[#D4AF37]/40"
                  }`}
                >
                  <span className="block font-display text-lg text-ink leading-tight" style={{ fontFamily: "'Tiro Telugu','Noto Serif Telugu','Yatra One',serif" }}>
                    {r.te}
                  </span>
                  <span className="block text-[11px] text-ink-soft/80 leading-tight mt-0.5">
                    {r.en}
                  </span>
                  <span className="block font-display text-sm text-plum leading-snug mt-1.5">
                    {roleTitle(r)}
                  </span>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="label-eyebrow block mb-2">{t("reg_photo_label", lang)}</label>
            <div
              className="relative rounded-3xl border-2 border-dashed border-[#D4AF37]/50 bg-white/40 transition-colors min-h-[200px] flex items-center justify-center overflow-hidden"
              data-testid="upload-selfie-zone"
            >
              {preview ? (
                <img src={preview} alt="selfie preview" className="w-full h-64 object-cover" />
              ) : (
                <div className="text-center px-5 py-7 w-full">
                  <div className="mx-auto w-12 h-12 rounded-full glass grid place-items-center mb-2">
                    <Camera className="text-[#B38728]" size={20} />
                  </div>
                  <p className="font-display text-base sm:text-lg text-plum">{t("reg_upload", lang)}</p>
                  <p className="text-xs text-ink-soft mt-1">{t("reg_upload_hint", lang)}</p>
                  <div className="mt-4 flex flex-col sm:flex-row gap-2 justify-center">
                    <button
                      type="button"
                      onClick={() => cameraRef.current?.click()}
                      className="btn-royal text-sm py-2 px-4"
                      data-testid="take-photo-btn"
                    >
                      <Camera size={14} />
                      {lang === "te" ? "ఫోటో తీయండి" : "Take Photo"}
                    </button>
                    <button
                      type="button"
                      onClick={() => fileRef.current?.click()}
                      className="btn-ghost-royal text-sm py-2 px-4"
                      data-testid="upload-file-btn"
                    >
                      <ImagePlus size={14} />
                      {lang === "te" ? "గ్యాలరీ నుండి ఎంచుకోండి" : "Upload from Device"}
                    </button>
                  </div>
                </div>
              )}
              <input
                ref={cameraRef}
                data-testid="capture-selfie-input"
                type="file"
                accept="image/*"
                capture="user"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) {
                    setRawFile(f);
                    setShowCropper(true);
                  }
                  e.target.value = "";
                }}
              />
              <input
                ref={fileRef}
                data-testid="upload-selfie-input"
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) {
                    setRawFile(f);
                    setShowCropper(true);
                  }
                  e.target.value = "";
                }}
              />
            </div>
            {preview && (
              <div className="mt-2 flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setShowCropper(true)}
                  className="text-xs label-eyebrow hover:text-plum"
                  data-testid="recrop-selfie-btn"
                >
                  {t("reg_recrop", lang)}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setFile(null);
                    setRawFile(null);
                  }}
                  className="text-xs label-eyebrow hover:text-plum"
                  data-testid="clear-selfie-btn"
                >
                  {t("reg_change", lang)}
                </button>
              </div>
            )}
          </div>

          {error && (
            <div data-testid="register-error" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-4 py-2">
              {error}
            </div>
          )}

          <button type="submit" disabled={submitting} data-testid="register-submit-btn" className="btn-royal w-full text-base">
            {submitting ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                {t("reg_submitting", lang)}
              </>
            ) : (
              <>
                <Crown size={18} />
                {t("reg_submit", lang)}
                <SparklesIcon size={18} />
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
}
